import React, { useEffect, useState } from "react";
import {
    Dialog, AppBar, Toolbar, IconButton, Typography, Slide, Box, Stack, Paper, Chip,
    CircularProgress, Alert, Table, TableHead, TableBody, TableRow, TableCell,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import livechatApi from "./livechatApi";
import { PASTEL } from "./livechatTheme";

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const formatDuration = (ms) => {
    if (ms == null) return "-";
    const minutes = Math.round(ms / 60000);
    if (minutes < 60) return `${minutes} menit`;
    const hours = Math.floor(minutes / 60);
    const remMinutes = minutes % 60;
    return remMinutes ? `${hours} jam ${remMinutes} menit` : `${hours} jam`;
};

// Fix (2026-09-18) — 3 pilihan rentang tanggal ini ketinggalan dari mockup tiket asli (cek gambar
// mockup, bukan cuma teks AC-5). "today" biarkan backend pakai default-nya sendiri (hari ini GMT+7,
// lihat parseDateRange di controller) — cuma "7d"/"30d" yang eksplisit ngirim from/to dari FE.
const RANGE_OPTIONS = [
    { key: "today", label: "Hari ini", days: null },
    { key: "7d", label: "7 hari", days: 7 },
    { key: "30d", label: "30 hari", days: 30 },
];

const buildRangeParams = (rangeKey) => {
    const option = RANGE_OPTIONS.find((o) => o.key === rangeKey);
    if (!option || option.days == null) return {};
    const to = new Date();
    const from = new Date(to.getTime() - option.days * 24 * 60 * 60 * 1000);
    return { from: from.toISOString(), to: to.toISOString() };
};

// Fix (2026-09-18) — cross-check ulang ke mockup asli (bukan sekadar tebakan): background kotak
// pink-beige (`dashboardWidget`, SAMA dengan header tabel di bawah), label warna emas/mustard
// (`accentLabel`), angka warna gelap netral (BUKAN pink/mauve — punya sebelumnya salah pakai
// `PASTEL.primary`). `noWrap` mencegah label panjang turun ke baris ke-2 yang nggeser posisi angka
// di kotak itu doang, jadi gak sejajar sama kotak lain yang labelnya pendek.
// Sengaja `Box` polos (BUKAN `Paper`/elevation sendiri) — di mockup ini cuma kotak warna DI DALAM
// 1 card besar yang sama (lihat pembungkus Paper tunggal di return di bawah), bukan card terpisah.
const SummaryCard = ({ label, value, caption }) => (
    <Box sx={{ p: 2.5, borderRadius: 2, backgroundColor: PASTEL.dashboardWidget, height: "100%" }}>
        <Typography
            variant="overline"
            noWrap
            sx={{ fontWeight: 700, display: "block", color: PASTEL.accentLabel }}
        >
            {label}
        </Typography>
        <Typography variant="h4" fontWeight={700} color="text.primary">
            {value}
        </Typography>
        {caption && (
            <Typography variant="caption" color="text.secondary">
                {caption}
            </Typography>
        )}
    </Box>
);

/**
 * DEV-1213/1214 — dashboard performa sales channel Corporate, khusus role Leader/SPV
 * (dicek juga di backend). "Chat aktif" & "rata-rata respon" per sales dihitung dari siapa
 * yang PERTAMA membalas tiap sesi — tidak ada assignment formal (round-robin didescope, lihat
 * task md), jadi tidak ada konsep "chat milik siapa" di luar itu.
 */
export default function LiveChatDashboardDialog({ open, onClose }) {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [rangeKey, setRangeKey] = useState("today");

    // Reset ke "Hari ini" tiap dialog dibuka ulang, biar gak nyangkut dari sesi buka sebelumnya.
    useEffect(() => {
        if (open) setRangeKey("today");
    }, [open]);

    useEffect(() => {
        if (!open) return;
        setIsLoading(true);
        setError(null);
        livechatApi
            .getDashboardPerformance({ brand: "corporate", ...buildRangeParams(rangeKey) })
            .then(({ data: res }) => setData(res.data))
            .catch((err) => {
                setError(
                    err?.response?.status === 403
                        ? "Anda tidak memiliki akses ke dashboard ini."
                        : "Gagal memuat data dashboard."
                );
            })
            .finally(() => setIsLoading(false));
    }, [open, rangeKey]);

    return (
        <Dialog fullScreen open={open} onClose={onClose} TransitionComponent={Transition}>
            <AppBar sx={{ position: "relative", backgroundColor: PASTEL.primary }}>
                <Toolbar>
                    <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
                        <CloseIcon />
                    </IconButton>
                    <Typography sx={{ ml: 2, flex: 1 }} variant="h6">
                        Dashboard Performa — Live Chat Corporate
                    </Typography>
                </Toolbar>
            </AppBar>

            <Box sx={{ p: 3, backgroundColor: PASTEL.background, minHeight: "100%" }}>
                {isLoading && (
                    <Box textAlign="center" sx={{ py: 6 }}>
                        <CircularProgress />
                    </Box>
                )}

                {!isLoading && error && (
                    <Alert severity="error" sx={{ borderRadius: 2 }}>
                        {error}
                    </Alert>
                )}

                {!isLoading && !error && data && (
                    // Fix (2026-09-18) — SEMUA (filter tanggal, 4 kotak ringkasan, tabel) dibungkus jadi
                    // SATU card besar (1 Paper), sesuai mockup asli — sebelumnya kepisah jadi 2 Paper
                    // (kotak ringkasan tanpa card sendiri-sendiri floating di background, tabel Paper
                    // terpisah). Bukan 2-3 card kecil, 1 card doang isinya semua.
                    // Fix (2026-09-18) — maxWidth diperlebar dari 900 -> 1400 biar konten gak kesempitan
                    // di kiri-kanan, khususnya di layar lebar (dialog ini fullScreen, ruang tersedia
                    // banyak).
                    <Paper sx={{ p: 3, borderRadius: 2, backgroundColor: PASTEL.surface, maxWidth: 1400, mx: "auto" }}>
                        <Stack spacing={3}>
                            {/* Pill BULAT PENUH yang TERPISAH (ada jarak antar tombol), bukan segmented
                            control nempel kayak ToggleButtonGroup bawaan MUI. */}
                            <Stack direction="row" spacing={1}>
                                {RANGE_OPTIONS.map((opt) => {
                                    const selected = rangeKey === opt.key;
                                    return (
                                        <Chip
                                            key={opt.key}
                                            label={opt.label}
                                            clickable
                                            onClick={() => setRangeKey(opt.key)}
                                            sx={{
                                                borderRadius: "999px",
                                                fontWeight: 600,
                                                px: 1,
                                                backgroundColor: selected ? PASTEL.primary : PASTEL.surface,
                                                color: selected ? "#fff" : "text.primary",
                                                border: selected ? "none" : `1px solid ${PASTEL.border}`,
                                                "&:hover": {
                                                    backgroundColor: selected ? PASTEL.primaryDark : PASTEL.surfaceAlt,
                                                },
                                            }}
                                        />
                                    );
                                })}
                            </Stack>

                            <Stack direction="row" spacing={2}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <SummaryCard label="Chat Aktif" value={data.activeChatsTotal} />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <SummaryCard
                                        label="Belum Terjawab"
                                        value={data.unansweredActiveChats}
                                        caption="Belum ada sales yang membalas — belum bisa diatribusikan ke sales tertentu."
                                    />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <SummaryCard
                                        label="Avg Respon Pertama"
                                        value={formatDuration(data.avgFirstResponseMsBrand)}
                                    />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <SummaryCard label="Total Chat" value={data.totalChatsInRange} />
                                </Box>
                            </Stack>

                            <Box>
                                <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, display: "block", mb: 1.5 }}>
                                    Performa per Sales (dihitung dari siapa yang pertama membalas tiap sesi)
                                </Typography>

                                {data.sales.length === 0 ? (
                                    <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                                        Belum ada data balasan sales pada rentang ini.
                                    </Alert>
                                ) : (
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: PASTEL.dashboardWidget }}>
                                                <TableCell sx={{ fontWeight: 700, color: PASTEL.tableHeaderText }}>Sales</TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 700, color: PASTEL.tableHeaderText }}>
                                                    Chat Aktif
                                                </TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 700, color: PASTEL.tableHeaderText }}>
                                                    Total Chat Dijawab
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700, color: PASTEL.tableHeaderText }}>
                                                    Rata-rata Waktu Respon Pertama
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {data.sales.map((row) => (
                                                <TableRow key={row.agentId}>
                                                    {/* Fix (2026-09-18) — mockup nunjukin nama sales sebagai teks
                                                    biasa (bold), bukan pill/Chip — punya sebelumnya salah bungkus
                                                    Chip (peninggalan implementasi awal, bukan sesuai mockup). */}
                                                    <TableCell sx={{ fontWeight: 600 }}>
                                                        {row.agentName || `Agent #${row.agentId}`}
                                                    </TableCell>
                                                    <TableCell align="center">{row.activeChats}</TableCell>
                                                    <TableCell align="center">{row.totalFirstResponses}</TableCell>
                                                    <TableCell align="right">{formatDuration(row.avgFirstResponseMs)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </Box>
                        </Stack>
                    </Paper>
                )}
            </Box>
        </Dialog>
    );
}
